import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../../../components/Breadcrumbs';
import Button from '../../../components/button';
import CustomTabs from '../../../components/CustomTabs';
import TransferStockModal from './TransferStockModal';
import FileUpload from '../../../components/FileUpload';
import Input from '../../../components/input';
import PhoneNumberInput from '../../../components/PhoneNumberInput';
import NewTableComponent from '../../../components/NewTableComponent';
import GoogleMapInput from '../../../components/GoogleMapInput';
import { activeDetailsIcon, detailsIcon } from '../../../utils/svgs';
import '../../../styles/Trip.css';
import PermissionWrapper from '../../../components/PermissionWrapper';
import { AppMainModules, InventorySubModules, VendorAndWarehousePermission } from '../../../utils/app-constant';
import { getWarehouseDetail, updateWarehouse, getWarehousePartsPaginated, getStockMovementInfo, deleteStockMovementInfo, changeStockMovementStatus } from '../../../store/Slices/PartVendorSlice';
import { successNotify, errorNotify } from '../../../utils/toast';
import { Formik } from 'formik';
import { WarehouseSchema } from '../../../utils/Schema';
import { PiPencilSimpleLine, PiTrash } from 'react-icons/pi';
import { normalizePhoneNumberValue } from '../../../pages/ThreePL/shared/phoneValidation';

const inventoryTabIcon = (active) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6H4C3.44772 6 3 6.44772 3 7V20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20V7C21 6.44772 20.5523 6 20 6Z" stroke={active ? '#1447B1' : '#4D4D4D'} strokeWidth="1.25" strokeLinejoin="round" />
        <path d="M8.975 12.004H14.975M3 6.5L6.5 2.5H17.5L21 6.5" stroke={active ? '#1447B1' : '#4D4D4D'} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const transferTabIcon = (active) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 5.5H11" stroke={active ? '#1447B1' : '#4D4D4D'} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M9.5 3.5L11.5 5.5L9.5 7.5" stroke={active ? '#1447B1' : '#4D4D4D'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13.5 10.5H5" stroke={active ? '#1447B1' : '#4D4D4D'} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M6.5 8.5L4.5 10.5L6.5 12.5" stroke={active ? '#1447B1' : '#4D4D4D'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const formatPartId = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    const normalized = String(value).trim();
    if (!normalized) return '-';
    if (/^P-/i.test(normalized)) return normalized;
    return `P-${normalized}`;
};

const formatApiDate = (value) => {
    if (!value) return '-';
    return String(value).split('T')[0] || '-';
};

const toFiniteNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const firstFiniteNumber = (...values) => {
    for (const value of values) {
        const numericValue = toFiniteNumber(value);
        if (numericValue !== null) return numericValue;
    }
    return null;
};

const firstNonEmptyString = (...values) => {
    for (const value of values) {
        if (value === undefined || value === null) continue;
        const normalized = String(value).trim();
        if (normalized) return normalized;
    }
    return '';
};

const resolveWarehouseName = (...candidates) => {
    for (const candidate of candidates) {
        if (!candidate) continue;
        if (typeof candidate === 'string') {
            const normalized = candidate.trim();
            if (normalized) return normalized;
            continue;
        }
        const resolved = firstNonEmptyString(
            candidate?.warehouse?.name,
            candidate?.warehouse_name,
            candidate?.name,
            candidate?.label
        );
        if (resolved) return resolved;
    }
    return '';
};

const resolveTransferItems = (item) => {
    const candidates = [
        item?.items,
        item?.transfer_items,
        item?.part_items,
        item?.stock_movement_items,
        item?.part_stock_movements,
    ];
    const arrayCandidates = candidates.filter((entry) => Array.isArray(entry));
    const matchedWithData = arrayCandidates.find((entry) => entry.length > 0);
    return matchedWithData || arrayCandidates[0] || [];
};

const resolveTransferPart = (item, transferItems) => {
    if (item?.part) return item.part;
    if (item?.part_detail) return item.part_detail;
    if (item?.part_details) return item.part_details;
    if (item?.part_stock?.part) return item.part_stock.part;

    for (const transferItem of transferItems) {
        if (transferItem?.part) return transferItem.part;
        if (transferItem?.part_detail) return transferItem.part_detail;
        if (transferItem?.part_details) return transferItem.part_details;
        if (transferItem?.part_stock?.part) return transferItem.part_stock.part;
    }

    return null;
};

const resolveTransferQuantity = (item, transferItems) => {
    const directQuantity = firstFiniteNumber(
        item?.quantity,
        item?.transfer_quantity,
        item?.qty,
        item?.item_quantity,
        item?.total_quantity
    );
    if (directQuantity !== null) return directQuantity;

    if (!transferItems.length) return 0;

    return transferItems.reduce((sum, transferItem) => {
        const rowQty = firstFiniteNumber(
            transferItem?.quantity,
            transferItem?.transfer_quantity,
            transferItem?.qty,
            transferItem?.item_quantity
        );
        return sum + (rowQty ?? 0);
    }, 0);
};

const getTransferStatusMeta = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'in_transit') return { label: 'In-Transit', className: 'in_transit', icon: 'clock' };
    if (normalized === 'pending') return { label: 'Pending', className: 'pending', icon: 'pending' };
    if (normalized === 'completed') return { label: 'Completed', className: 'received', icon: 'check' };
    if (normalized === 'cancelled') return { label: 'Cancelled', className: 'cancelled', icon: 'cancel' };
    return { label: status || '-', className: '', icon: null };
};

const renderTransferStatusIcon = (statusMeta) => {
    if (statusMeta.icon === 'clock') {
        return (
            <svg viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" />
                <path d="M8 4.2V8L10.8 9.4" />
            </svg>
        );
    }
    if (statusMeta.icon === 'pending') {
        return (
            <svg viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" />
                <path d="M8 4.2V8.2" />
                <circle cx="8" cy="11" r="0.6" fill="currentColor" stroke="none" />
            </svg>
        );
    }
    if (statusMeta.icon === 'check') {
        return (
            <svg viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" />
                <path d="M5.2 8.2L7.1 10.1L11 6.2" />
            </svg>
        );
    }
    if (statusMeta.icon === 'cancel') {
        return (
            <svg viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" />
                <path d="M4.8 11.2L11.2 4.8" />
            </svg>
        );
    }
    return null;
};

const WarehouseDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    const heading = 'Warehouse Details';
    const requiredStar = <span className="required_star">*</span>;

    const navigateToWarehouseTab = () => {
        navigate('/warehouse');
    };

    const initialRecord = useMemo(() => {
        const data = location.state?.data || {};
        return {
            warehouseId: data.warehouseId || data.id || '',
            name: data.name || data.warehouseName || '',
            address: {
                warehouse_point: data.location || data.address || '',
                warehouse_latitude: data.lat || '',
                warehouse_longitude: data.lng || ''
            },
            pocName: data.poc_name || data.primaryPOC || '',
            pocContact: normalizePhoneNumberValue(data.poc_contact || data.contactNumber || ''),
            pocEmail: data.poc_email || data.email || '',
            notes: data.notes || '',
            attachments: []
        };
    }, [location.state]);

    const [formInitialValues, setFormInitialValues] = useState(initialRecord);
    const [activeTab, setActiveTab] = useState('general');
    const [inventoryPage, setInventoryPage] = useState(1);
    const [inventoryLimit, setInventoryLimit] = useState(10);
    const [transferPage, setTransferPage] = useState(1);
    const [transferLimit, setTransferLimit] = useState(10);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const lastInventoryRequestKeyRef = useRef('');
    const lastTransferRequestKeyRef = useRef('');
    const [transferStatusMap, setTransferStatusMap] = useState({});
    const [statusDropdownState, setStatusDropdownState] = useState({ open: false, rowId: null });
    const statusDropdownRef = useRef(null);

    // Selectors
    const {
        warehouseDetail,
        updateWarehouseLoading,
        warehousePartsPaginatedData,
        getWarehousePartsLoading,
        stockMovementInfoData,
        getStockMovementInfoLoading
    } = useSelector((state) => state.partVendors);

    useEffect(() => {
        if (params.id) {
            dispatch(getWarehouseDetail(params.id));
        }
    }, [dispatch, params.id]);

    useEffect(() => {
        if (warehouseDetail) {
            const data = warehouseDetail;
            setFormInitialValues({
                warehouseId: data.warehouse_id || data.id,
                name: data.name,
                address: {
                    warehouse_point: data.location,
                    warehouse_latitude: data.lat,
                    warehouse_longitude: data.lng
                },
                pocName: data.poc_name,
                pocContact: normalizePhoneNumberValue(data.poc_contact),
                pocEmail: data.poc_email,
                notes: data.notes || '',
                attachments: []
            });
            setAttachments((data.files || []).map(f => {
                if (typeof f === 'string') {
                    return { fileName: f, originalName: f };
                }
                const name = f.fileName || f.file_name || f.file_path || f.url || f.path || f.file || f.image || '';
                return {
                    ...f,
                    fileName: name,
                    originalName: f.originalName || f.original_name || name
                };
            }));
        }
    }, [warehouseDetail, initialRecord.notes]);

    const resolvedWarehouseId = useMemo(
        () => Number(warehouseDetail?.id || warehouseDetail?.warehouse_id || params.id),
        [warehouseDetail?.id, warehouseDetail?.warehouse_id, params.id]
    );

    useEffect(() => {
        if (activeTab !== 'inventory') return;
        if (!resolvedWarehouseId) return;

        const requestKey = `${resolvedWarehouseId}-${inventoryPage}-${inventoryLimit}`;
        if (lastInventoryRequestKeyRef.current === requestKey) return;

        lastInventoryRequestKeyRef.current = requestKey;

        dispatch(getWarehousePartsPaginated({
            page: inventoryPage,
            limit: inventoryLimit,
            warehouse_id: resolvedWarehouseId
        }))
            .unwrap()
            .catch(() => {
                lastInventoryRequestKeyRef.current = '';
            });
    }, [dispatch, activeTab, resolvedWarehouseId, inventoryPage, inventoryLimit]);

    useEffect(() => {
        if (activeTab !== 'transfer') return;
        if (!resolvedWarehouseId) return;

        const requestKey = `${resolvedWarehouseId}-${transferPage}-${transferLimit}`;
        if (lastTransferRequestKeyRef.current === requestKey) return;

        lastTransferRequestKeyRef.current = requestKey;

        dispatch(getStockMovementInfo({
            page: transferPage,
            limit: transferLimit,
            warehouse_id: resolvedWarehouseId
        }))
            .unwrap()
            .catch(() => {
                lastTransferRequestKeyRef.current = '';
            });
    }, [dispatch, activeTab, resolvedWarehouseId, transferPage, transferLimit]);

    useEffect(() => {
        if (activeTab !== 'inventory') {
            lastInventoryRequestKeyRef.current = '';
        }
        if (activeTab !== 'transfer') {
            lastTransferRequestKeyRef.current = '';
        }
    }, [activeTab, resolvedWarehouseId]);

    useEffect(() => {
        const closeStatusMenu = (e) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
                setStatusDropdownState({ open: false, rowId: null });
            }
        };
        if (statusDropdownState.open) {
            document.addEventListener('mousedown', closeStatusMenu);
        }
        return () => document.removeEventListener('mousedown', closeStatusMenu);
    }, [statusDropdownState.open]);

    useEffect(() => {
        setTransferStatusMap({});
    }, [stockMovementInfoData]);

    const inventoryColumns = [
        {
            name: 'Part ID',
            selector: (row) => row.partId,
            cell: (row) => <span className="warehouse_inventory_link">{formatPartId(row.partId)}</span>,
            sortable: false,
            width: '120px',
            tableColumn: false,
        },
        {
            name: 'Part Name',
            selector: (row) => row.name,
            sortable: false,
            grow: 2,
            tableColumn: false,
        },
        {
            name: 'Current Stock',
            selector: (row) => row.stock,
            sortable: false,
            width: '140px',
            tableColumn: false,
        },
        {
            name: 'Last Updated',
            selector: (row) => row.updated,
            sortable: false,
            width: '140px',
            tableColumn: false,
        },
    ];

    const inventoryPageData = useMemo(() => {
        const rows = warehousePartsPaginatedData?.results || [];
        return rows.map((item) => ({
            partId: item?.id ?? '-',
            name: item?.name || item?.part_number || item?.description || '-',
            stock: item?.part_stock?.available_qty ?? item?.stock_count ?? 0,
            updated: item?.part_stock?.updated_at
                ? new Date(item.part_stock.updated_at).toLocaleDateString()
                : item?.updated_at
                    ? new Date(item.updated_at).toLocaleDateString()
                    : '-'
        }));
    }, [warehousePartsPaginatedData]);

    const inventoryTotalItems = warehousePartsPaginatedData?.meta?.totalItems
        || warehousePartsPaginatedData?.total
        || inventoryPageData.length;
    const inventoryTotalPages = warehousePartsPaginatedData?.meta?.totalPages
        || Math.max(1, Math.ceil(inventoryTotalItems / inventoryLimit));

    const transferColumns = [
        {
            name: 'Source Warehouse',
            selector: (row) => row.source,
            cell: (row) => <span style={{ whiteSpace: 'normal', lineHeight: '1.25' }}>{row.source}</span>,
            sortable: false,
            minWidth: '220px',
            wrap: true,
            tableColumn: false,
        },
        {
            name: 'Destination',
            selector: (row) => row.destination,
            cell: (row) => <span style={{ whiteSpace: 'normal', lineHeight: '1.25' }}>{row.destination}</span>,
            sortable: false,
            minWidth: '220px',
            wrap: true,
            tableColumn: false,
        },
        {
            name: 'Part Detail',
            selector: (row) => row?.part?.name || row?.part?.part_number || row?.part?.description || '-',
            cell: (row) => (
                <span style={{ whiteSpace: 'normal', lineHeight: '1.25', display: 'block' }}>
                    <span className={row.partCount > 1 ? 'transfer_pill' : ''}>
                        {row.partCount > 1 && (
                            <svg viewBox="0 0 16 16" aria-hidden="true">
                                <path d="M3 6.5L8 3.5L13 6.5L8 9.5L3 6.5Z" />
                                <path d="M3 6.5V12.5L8 15.5L13 12.5V6.5" />
                                <path d="M8 9.5V15.5" />
                            </svg>
                        )}
                        {row.partDetail}
                    </span>
                </span>
            ),
            sortable: false,
            minWidth: '170px',
            wrap: true,
            tableColumn: false,
        },
        {
            name: 'Quantity',
            selector: (row) => row.quantity,
            sortable: false,
            width: '110px',
            tableColumn: false,
        },
        {
            name: 'Last Updated',
            selector: (row) => row.lastUpdated,
            sortable: false,
            width: '130px',
            tableColumn: false,
        },
        {
            name: 'Status',
            selector: (row) => row.transfer_status,
            cell: (row) => {
                const effectiveStatus = transferStatusMap[row.id] || row.transfer_status;
                const statusMeta = getTransferStatusMeta(effectiveStatus);
                const normalizedEffectiveStatus = String(effectiveStatus || '').toLowerCase().trim();
                const isStatusLocked = normalizedEffectiveStatus === 'completed';
                const normalizeStatus = (s) => {
                    const n = String(s || '').toLowerCase().trim();
                    if (n === 'completed') return 'completed';
                    return n;
                };
                const statusOptions = [
                    { value: 'in_transit', ...getTransferStatusMeta('in_transit') },
                    { value: 'pending', ...getTransferStatusMeta('pending') },
                    { value: 'completed', ...getTransferStatusMeta('completed') },
                ].filter(opt => normalizeStatus(opt.value) !== normalizeStatus(effectiveStatus));

                return (
                    <div
                        className="transfer_status_dropdown"
                        ref={statusDropdownState.open && statusDropdownState.rowId === row.id ? statusDropdownRef : null}
                    >
                        <button
                            type="button"
                            className={`transfer_status_trigger ${isStatusLocked ? 'disabled' : ''}`}
                            disabled={isStatusLocked}
                            onClick={(e) => {
                                if (isStatusLocked) return;
                                e.stopPropagation();
                                setStatusDropdownState((prev) => ({
                                    open: !(prev.open && prev.rowId === row.id),
                                    rowId: row.id
                                }));
                            }}
                        >
                            <span className={`transfer_status ${statusMeta.className}`}>
                                {renderTransferStatusIcon(statusMeta)}
                                {statusMeta.label}
                            </span>
                        </button>

                        {!isStatusLocked && statusDropdownState.open && statusDropdownState.rowId === row.id && (
                            <div className="transfer_status_menu">
                                {statusOptions.map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className="transfer_status_option"
                                        onClick={async () => {
                                            setStatusDropdownState({ open: false, rowId: null });
                                            try {
                                                await dispatch(changeStockMovementStatus({
                                                    id:row.id,
                                                    transfer_status: opt.value
                                                })).unwrap();
                                                setTransferStatusMap((prev) => ({ ...prev, [row.id]: opt.value }));
                                                successNotify('Status updated successfully');
                                                handleTransferSuccess();
                                            } catch (error) {
                                                errorNotify(error || 'Failed to update status');
                                            }
                                        }}
                                    >
                                        <span className={`transfer_status ${opt.className}`}>
                                            {renderTransferStatusIcon(opt)}
                                            {opt.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                );
            },
            sortable: false,
            minWidth: '170px',
            tableColumn: false,
        },
        {
            name: 'Action',
            selector: (row) => row.action,
            cell: (row) => {
                const effectiveStatus = transferStatusMap[row.id] || row.transfer_status;
                const normalized = String(effectiveStatus || '').toLowerCase().trim();
                const isDisabled = normalized === 'completed' || normalized === 'in_transit';

                const handleDelete = async () => {
                    if (isDisabled) return;
                    try {
                        await dispatch(deleteStockMovementInfo(row.id)).unwrap();
                        successNotify('Stock movement deleted successfully');
                        handleTransferSuccess();
                    } catch (error) {
                        errorNotify(error || 'Failed to delete stock movement');
                    }
                };

                return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            color: isDisabled ? '#ccc' : '#666',
                            opacity: isDisabled ? 0.5 : 1
                        }}>
                            <PiPencilSimpleLine size={18} />
                        </span>
                        <span
                            onClick={handleDelete}
                            style={{
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                color: isDisabled ? '#ccc' : '#FA6262',
                                opacity: isDisabled ? 0.5 : 1
                            }}
                        >
                            <PiTrash size={18} />
                        </span>
                    </div>
                );
            },
            sortable: false,
            width: '110px',
            tableColumn: false,
            ignoreRowClick: true,
        },
    ];

    const transferPageData = useMemo(() => {
        const rows = Array.isArray(stockMovementInfoData?.results) ? stockMovementInfoData.results : [];
        const currentWarehouseId = toFiniteNumber(resolvedWarehouseId);
        const currentWarehouseName = firstNonEmptyString(warehouseDetail?.name, formInitialValues?.name);

        return rows.map((item) => {
            const transferItems = resolveTransferItems(item);
            const part = resolveTransferPart(item, transferItems);

            const sourceWarehouseEntity =
                item?.from_warehouse ||
                item?.source_warehouse ||
                null;
            const destinationWarehouseEntity =
                item?.to_warehouse ||
                item?.destination_warehouse ||
                null;
            const referenceWarehouseEntity =
                item?.part_stock_movement_destination?.warehouse ||
                item?.part_stock_movement_destination ||
                item?.part_stock_movement_reference?.warehouse ||
                item?.part_stock_movement_reference ||
                item?.warehouse ||
                null;

            const sourceWarehouseId = firstFiniteNumber(
                item?.from_warehouse_id,
                item?.source_warehouse_id,
                sourceWarehouseEntity?.id,
                sourceWarehouseEntity?.warehouse_id
            );
            const destinationWarehouseId = firstFiniteNumber(
                item?.to_warehouse_id,
                item?.destination_warehouse_id,
                destinationWarehouseEntity?.id,
                destinationWarehouseEntity?.warehouse_id
            );

            const sourceWarehouseName = resolveWarehouseName(
                sourceWarehouseEntity,
                item?.from_warehouse_name,
                item?.source_warehouse_name
            );
            const destinationWarehouseName = resolveWarehouseName(
                destinationWarehouseEntity,
                item?.to_warehouse_name,
                item?.destination_warehouse_name
            );
            const referenceWarehouseName = resolveWarehouseName(
                referenceWarehouseEntity,
                item?.reference_warehouse_name,
                item?.warehouse_name
            );

            const sourceWarehouse = currentWarehouseName || sourceWarehouseName || '-';
            let destinationWarehouse = firstNonEmptyString(
                destinationWarehouseName,
                item?.destination,
                item?.destination_name
            );

            if (!destinationWarehouse) {
                if (
                    currentWarehouseId !== null &&
                    sourceWarehouseId !== null &&
                    sourceWarehouseId !== currentWarehouseId &&
                    sourceWarehouseName
                ) {
                    destinationWarehouse = sourceWarehouseName;
                } else if (
                    currentWarehouseId !== null &&
                    destinationWarehouseId !== null &&
                    destinationWarehouseId !== currentWarehouseId &&
                    destinationWarehouseName
                ) {
                    destinationWarehouse = destinationWarehouseName;
                } else {
                    destinationWarehouse = referenceWarehouseName;
                }
            }

            const normalizedSource = String(sourceWarehouse || '').trim().toLowerCase();
            const normalizedDestination = String(destinationWarehouse || '').trim().toLowerCase();
            if (normalizedSource && normalizedSource === normalizedDestination) {
                destinationWarehouse = firstNonEmptyString(
                    sourceWarehouseName && sourceWarehouseName.trim().toLowerCase() !== normalizedSource ? sourceWarehouseName : '',
                    destinationWarehouseName && destinationWarehouseName.trim().toLowerCase() !== normalizedSource ? destinationWarehouseName : '',
                    referenceWarehouseName && referenceWarehouseName.trim().toLowerCase() !== normalizedSource ? referenceWarehouseName : '',
                    destinationWarehouse
                );
            }

            const partDetail = firstNonEmptyString(
                part?.name,
                part?.part_number,
                part?.description,
                item?.part_name,
                item?.part_number,
                item?.description,
                transferItems[0]?.part?.name,
                transferItems[0]?.part?.part_number,
                transferItems[0]?.part?.description
            ) || '-';
            const partCount = firstFiniteNumber(
                item?.part_count,
                item?.items_count,
                transferItems.length
            ) ?? (partDetail !== '-' ? 1 : 0);
            const quantity = resolveTransferQuantity(item, transferItems);
            const transferStatus = firstNonEmptyString(item?.transfer_status, item?.status) || '-';

            return {
                id: item?.id ?? item?.stock_movement_id ?? item?.transfer_id ?? item?.parts_stock_transfer_id ?? '-',
                source: sourceWarehouse || '-',
                destination: destinationWarehouse || '-',
                part,
                partDetail,
                partCount,
                quantity,
                lastUpdated: formatApiDate(item?.updated_at || item?.created_at || item?.transfer_date),
                transfer_status: transferStatus,
                action: String(transferStatus).toLowerCase() === 'pending' ? 'Confirm' : ''
            };
        });
    }, [stockMovementInfoData, resolvedWarehouseId, warehouseDetail?.name, formInitialValues?.name]);

    const transferTotalItems = stockMovementInfoData?.meta?.totalItems
        || stockMovementInfoData?.total
        || transferPageData.length;
    const transferTotalPages = stockMovementInfoData?.meta?.totalPages
        || Math.max(1, Math.ceil(transferTotalItems / transferLimit));
    const transferOverview = stockMovementInfoData?.overview || {};

    const warehouseTabs = [
        {
            key: 'general',
            label: 'General Details',
            icon: activeTab === 'general' ? activeDetailsIcon : detailsIcon,
        },
        {
            key: 'inventory',
            label: 'Inventory',
            icon: inventoryTabIcon(activeTab === 'inventory'),
        },
        {
            key: 'transfer',
            label: 'Transfer',
            icon: transferTabIcon(activeTab === 'transfer'),
        }
    ];

    const handleTransferSuccess = () => {
        if (!resolvedWarehouseId) return;
        lastInventoryRequestKeyRef.current = '';
        lastTransferRequestKeyRef.current = '';

        dispatch(getWarehousePartsPaginated({
            page: inventoryPage,
            limit: inventoryLimit,
            warehouse_id: resolvedWarehouseId
        }));

        dispatch(getStockMovementInfo({
            page: transferPage,
            limit: transferLimit,
            warehouse_id: resolvedWarehouseId
        }));
    };

    return (
        <Formik
            enableReinitialize={true}
            initialValues={formInitialValues}
            validationSchema={WarehouseSchema}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    const finalPayload = {
                        warehouse_id: warehouseDetail?.id,
                        name: values.name,
                        location: values.address?.warehouse_point,
                        lat: values.address?.warehouse_latitude,
                        lng: values.address?.warehouse_longitude,
                        poc_name: values.pocName,
                        poc_contact: values.pocContact,
                        poc_email: values.pocEmail,
                        status: 'active',
                        files: attachments.length > 0 ? attachments.map(f => f.fileName || f) : [],
                        notes: values.notes
                    };

                    await dispatch(updateWarehouse(finalPayload)).unwrap();
                    successNotify("Warehouse updated successfully");
                    navigateToWarehouseTab();
                } catch (error) {
                    errorNotify(error);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                isSubmitting
            }) => (
                <>
                    <Breadcrumbs values={['Vendor & Warehouse', 'Warehouse', heading]} />

                    <div className="page_header">
                        <h2>{heading}</h2>
                        <div className="d-flex align-items-center gap-2">
                            <PermissionWrapper
                                module={InventorySubModules.Warehouse}
                                permission={VendorAndWarehousePermission.UpdateWarehouse}
                                mainModule={AppMainModules.inventoryManagement}
                            >
                                <Button
                                    cancelBtn={navigateToWarehouseTab}
                                    submitText="Save Details"
                                    type="button"
                                    shouldRow={false}
                                    isShowSubmitBtn={true}
                                    submitBtn={handleSubmit}
                                    loading={updateWarehouseLoading}
                                    disabled={updateWarehouseLoading}
                                />
                            </PermissionWrapper>
                        </div>
                    </div>

                    <div className="page_container">
                        <Row>
                            <Col md={9}>
                                <div className="mb-3">
                                    <CustomTabs
                                        tabs={warehouseTabs}
                                        activeTab={activeTab}
                                        setActiveTab={setActiveTab}
                                    />
                                </div>

                                {activeTab === 'general' && (
                                    <>
                                        <div className="content_box">
                                            <h6>General Information</h6>
                                            <p>Main information for this record.</p>

                                            <Row>
                                                <Col md={6}>
                                                    <Input
                                                        label={<>Warehouse ID {requiredStar}</>}
                                                        placeholder="Enter Warehouse ID"
                                                        name="warehouseId"
                                                        value={values.warehouseId}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.warehouseId}
                                                        touched={touched.warehouseId}
                                                        required={false}
                                                        disabled={true}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Input
                                                        label={<>Warehouse Name {requiredStar}</>}
                                                        placeholder="Enter Warehouse Name"
                                                        name="name"
                                                        value={values.name}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.name}
                                                        touched={touched.name}
                                                        required={false}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <GoogleMapInput
                                                        label={<>Address {requiredStar}</>}
                                                        name="address"
                                                        value={values.address}
                                                        setFieldValue={setFieldValue}
                                                        error={errors.address}
                                                        touched={touched.address}
                                                        fieldPrefix="warehouse"
                                                        required={false}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Input
                                                        label={<>POC Name {requiredStar}</>}
                                                        placeholder="Enter POC Name"
                                                        name="pocName"
                                                        value={values.pocName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.pocName}
                                                        touched={touched.pocName}
                                                        required={false}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <PhoneNumberInput
                                                        label={<>POC Contact {requiredStar}</>}
                                                        placeholder="923XXXXXXXXX"
                                                        name="pocContact"
                                                        value={values.pocContact}
                                                        setFieldValue={setFieldValue}
                                                        onBlur={handleBlur}
                                                        error={errors.pocContact}
                                                        touched={touched.pocContact}
                                                        required={false}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Input
                                                        label={<>Email Address {requiredStar}</>}
                                                        placeholder="Enter Email Address"
                                                        name="pocEmail"
                                                        value={values.pocEmail}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.pocEmail}
                                                        touched={touched.pocEmail}
                                                        required={false}
                                                    />
                                                </Col>
                                            </Row>
                                        </div>

                                        <div className="content_box">
                                            <h6>Attachments</h6>
                                            <p>Upload relevant photos or documents for this service.</p>
                                            <div className="issue_file_upload_wrapper">
                                                <FileUpload
                                                    destination="vendor-attachments"
                                                    files={attachments}
                                                    setFiles={setAttachments}
                                                    maxFileSize={10}
                                                    className="issue_file_upload"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {activeTab === 'inventory' && (
                                    <div className="content_box">
                                        <h6>Inventory Log</h6>
                                        <p>Main information for this part record.</p>

                                        <div className="warehouse_inventory_table">
                                            <NewTableComponent
                                                columns={inventoryColumns}
                                                data={inventoryPageData}
                                                currentPage={inventoryPage}
                                                setCurrentPage={setInventoryPage}
                                                limit={inventoryLimit}
                                                setLimit={setInventoryLimit}
                                                totalItems={inventoryTotalItems}
                                                totalPages={inventoryTotalPages}
                                                loading={getWarehousePartsLoading}
                                                showFilter={false}
                                                showPagination={true}
                                                paginationLoading={getWarehousePartsLoading}
                                            />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'transfer' && (
                                    <div className="content_box">
                                        <div className="transfer_stat_row">
                                            <div className="transfer_stat_card"><span>Total Transfer</span><div className="transfer_stat_value total"><span className="stat_bar" /><h4>{transferOverview?.total ?? 0}</h4></div></div>
                                            <div className="transfer_stat_card"><span>In-Transit</span><div className="transfer_stat_value in_transit"><span className="stat_bar" /><h4>{transferOverview?.in_transit_total ?? 0}</h4></div></div>
                                            <div className="transfer_stat_card"><span>Pending</span><div className="transfer_stat_value pending"><span className="stat_bar" /><h4>{transferOverview?.pending_total ?? 0}</h4></div></div>
                                            <div className="transfer_stat_card"><span>Completed</span><div className="transfer_stat_value completed"><span className="stat_bar" /><h4>{transferOverview?.complete_total ?? 0}</h4></div></div>
                                        </div>

                                        <PermissionWrapper
                                            module={InventorySubModules.Warehouse}
                                            permission={VendorAndWarehousePermission.UpdateWarehouse}
                                            mainModule={AppMainModules.inventoryManagement}
                                        >
                                            <button
                                                type="button"
                                                className="transfer_stock_btn"
                                                onClick={() => setShowTransferModal(true)}
                                            >
                                                <svg viewBox="0 0 16 16" aria-hidden="true">
                                                    <path d="M3 4.5H13" />
                                                    <path d="M10.5 2.5L13 4.5L10.5 6.5" />
                                                    <path d="M13 11.5H3" />
                                                    <path d="M5.5 9.5L3 11.5L5.5 13.5" />
                                                </svg>
                                                Transfer Stock
                                            </button>
                                        </PermissionWrapper>

                                        <div className="content_box transfer_table_box">
                                            <h6>Transfer Information</h6>
                                            <p>An audit trail tracking the consumption of the part to ensure accountability.</p>

                                            <div className="warehouse_inventory_table">
                                                <NewTableComponent
                                                    columns={transferColumns}
                                                    data={transferPageData}
                                                    currentPage={transferPage}
                                                    setCurrentPage={setTransferPage}
                                                    limit={transferLimit}
                                                    setLimit={setTransferLimit}
                                                    totalItems={transferTotalItems}
                                                    totalPages={transferTotalPages}
                                                    loading={getStockMovementInfoLoading}
                                                    showFilter={false}
                                                    showPagination={true}
                                                    paginationLoading={getStockMovementInfoLoading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Col>
                            <Col md={3}>
                                <div className="service_cost_summary_details">
                                    <div className="parts_general_notes">
                                        <h5>General Notes</h5>
                                        <p>Add any additional notes or observations here.</p>
                                        <textarea
                                            rows="12"
                                            style={{
                                                width: '100%',
                                                border: '1px solid #E3E3E8',
                                                borderRadius: '8px',
                                                padding: '10px',
                                                background: '#F9F9FB',
                                                color: '#3D3F5C',
                                                fontSize: '14px'
                                            }}
                                            value={values.notes}
                                            onChange={handleChange}
                                            name="notes"
                                        />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <TransferStockModal
                        show={showTransferModal}
                        onHide={() => setShowTransferModal(false)}
                        onClose={() => setShowTransferModal(false)}
                        sourceWarehouseId={resolvedWarehouseId}
                        sourceWarehouseName={warehouseDetail?.name || formInitialValues?.name || '-'}
                        onTransferSuccess={handleTransferSuccess}
                    />
                </>
            )}
        </Formik>
    );
};

export default WarehouseDetails;
